import { useCallback } from 'react';
import { Vector3 } from 'three';
import { IBuildingProviderContext } from '../../../@types/ContextProviderRelated';
import { EElementType } from '../../../@types/Enums';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import { IAttribute } from '../../../models/standard/AttributeModel';
import ElementModel, { IElement } from '../../../models/standard/ElementModel';

export interface IElementActions {
  addElement: (
    type: EElementType, 
    length: number, 
    width: number, 
    position?: Vector3, 
    spaceUuid?: string,
    attributes?: IAttribute[],
    sideC?: number,
    triangleAngle?: number 
  ) => void;
  setElements: (elements: Array<IElement>) => void;
  deleteElement: (uuid: string) => void;
  updateElement: (index: number, updatedElement: ElementModel) => void;
  setElementRotation: (uuid: string, rotation: number) => void; 
  setPendingElement: (element: { type: EElementType; length: number; width: number } | null) => void;
}

export const useElementActions = (setBuilding: React.Dispatch<React.SetStateAction<IBuildingProviderContext>>): IElementActions => {
  
  // Helper to clone an object while keeping its Class Prototype (Methods, Getters/Setters)
  const cloneWithProto = <T extends object>(obj: T): T => Object.assign(Object.create(Object.getPrototypeOf(obj)), obj);

  const addElement = useCallback(
    (
      type: EElementType, 
      length: number, 
      width: number, 
      position?: Vector3, 
      spaceUuid?: string,
      attributes: IAttribute[] = [], 
      sideC?: number,                
      triangleAngle?: number         
    ): void => {
      
      const pos = position ? new Vector3(position.x, position.y, position.z) : new Vector3(0, 0, 0);
      
      const newElement = new ElementModel(
        type,
        length,
        width,
        pos,
        0,          // rotation (default)
        null,       // texture (optional)
        undefined,  // description (optional)
        false,      // useAsTextField (optional)
        undefined,  // fontSize (optional)
        undefined,  // object (optional)
        attributes, 
        sideC,      
        triangleAngle 
      );
      
      setBuilding((prevBuilding) => {
        const next = { ...prevBuilding };

        if (spaceUuid && next.rooms) {
          let addedToSpace = false;
          
          const newRooms = next.rooms.map((room) => {
            if (!room.spaces) return room;
            
            let roomModified = false;
            const newSpaces = room.spaces.map((space) => {
              if (space.uuid === spaceUuid) {
                addedToSpace = true;
                roomModified = true;
                const currentElements = space.elements || [];
                
                // Clone space correctly
                const updatedSpace = cloneWithProto(space);
                updatedSpace.elements = [...currentElements, newElement];
                return updatedSpace;
              }
              return space;
            });

            if (roomModified) {
               const updatedRoom = cloneWithProto(room);
               updatedRoom.spaces = newSpaces;
               return updatedRoom;
            }
            return room;
          });

          if (addedToSpace) {
            return { 
                ...next, 
                rooms: newRooms,
                pendingElement: null, 
                isSetByUndoRedo: false, 
                lastAction: EHistoryAction.ELEMENT_ADDED 
            };
          }
        }

        // Fallback: Add to root
        next.elements = [...(next.elements || []), newElement];
        return { 
            ...next, 
            pendingElement: null, 
            isSetByUndoRedo: false, 
            lastAction: EHistoryAction.ELEMENT_ADDED 
        };
      });
    },
    [setBuilding],
  );

  const setElements = useCallback(
    (elements: Array<IElement>): void => {
      setBuilding((prevBuilding) => ({
        ...prevBuilding,
        elements,
        isSetByUndoRedo: false,
        lastAction: EHistoryAction.ELEMENT_SET,
      }));
    },
    [setBuilding],
  );

  const deleteElement = useCallback(
    (uuidToDelete: string): void => {
      setBuilding((prevBuilding) => {
        const next = { ...prevBuilding };
        let found = false;

        // 1. Try Root
        if (next.elements) {
            const index = next.elements.findIndex((el) => el.uuid === uuidToDelete);
            if (index !== -1) {
                const newElements = [...next.elements];
                newElements.splice(index, 1);
                next.elements = newElements;
                found = true;
            }
        }

        // 2. Try Spaces
        if (!found && next.rooms) {
            next.rooms = next.rooms.map((room) => {
                if (!room.spaces) return room;
                let roomChanged = false;
                
                const newSpaces = room.spaces.map((space) => {
                    if (!space.elements) return space;
                    const index = space.elements.findIndex((el) => el.uuid === uuidToDelete);
                    if (index !== -1) {
                        found = true;
                        roomChanged = true;
                        
                        const newElements = [...space.elements];
                        newElements.splice(index, 1);
                        
                        const newSpace = cloneWithProto(space);
                        newSpace.elements = newElements;
                        return newSpace;
                    }
                    return space;
                });

                if (roomChanged) {
                    const newRoom = cloneWithProto(room);
                    newRoom.spaces = newSpaces;
                    return newRoom;
                }
                return room;
            });
        }

        if (!found) return prevBuilding;

        return { ...next, isSetByUndoRedo: false, lastAction: EHistoryAction.ELEMENT_DELETED };
      });
    },
    [setBuilding],
  );

  const updateElement = useCallback(
    (_index: number, updatedElement: ElementModel): void => {
      const targetUuid = updatedElement.uuid;
      if (!targetUuid) return;

      setBuilding((prevBuilding) => {
        const next = { ...prevBuilding };
        let found = false;

        // 1. Try Root
        if (next.elements) {
            const idx = next.elements.findIndex((el) => el.uuid === targetUuid);
            if (idx !== -1) {
                const newArr = [...next.elements];
                newArr[idx] = updatedElement;
                next.elements = newArr;
                found = true;
            }
        }

        // 2. Try Spaces
        if (!found && next.rooms) {
            next.rooms = next.rooms.map((room) => {
                if (!room.spaces) return room;
                let roomChanged = false;
                
                const newSpaces = room.spaces.map((space) => {
                    if (!space.elements) return space;
                    const idx = space.elements.findIndex((el) => el.uuid === targetUuid);
                    
                    if (idx !== -1) {
                        found = true;
                        roomChanged = true;
                        const newArr = [...space.elements];
                        newArr[idx] = updatedElement;
                        
                        const newSpace = cloneWithProto(space);
                        newSpace.elements = newArr;
                        return newSpace;
                    }
                    return space;
                });

                if (roomChanged) {
                    const newRoom = cloneWithProto(room);
                    newRoom.spaces = newSpaces;
                    return newRoom;
                }
                return room;
            });
        }

        if (!found) {
            console.warn(`Element with UUID ${targetUuid} not found for update.`);
            return prevBuilding;
        }

        return { ...next, isSetByUndoRedo: false, lastAction: EHistoryAction.ELEMENT_UPDATED };
      });
    },
    [setBuilding],
  );

  const setElementRotation = useCallback(
    (uuid: string, rotation: number): void => {
      setBuilding((prevBuilding) => {
        const next = { ...prevBuilding };
        let found = false;

        // 1. Try Root
        if (next.elements) {
          const idx = next.elements.findIndex((el) => el.uuid === uuid);
          if (idx !== -1) {
            const newArr = [...next.elements];
            // FIX: Don't use spread syntax {...el}, use cloneWithProto to preserve Class instance
            const updatedEl = cloneWithProto(newArr[idx]);
            updatedEl.rotation = rotation;
            
            newArr[idx] = updatedEl;
            next.elements = newArr;
            found = true;
          }
        }

        // 2. Try Spaces
        if (!found && next.rooms) {
          next.rooms = next.rooms.map((room) => {
            if (!room.spaces) return room;
            let roomChanged = false;

            const newSpaces = room.spaces.map((space) => {
              if (!space.elements) return space;
              const idx = space.elements.findIndex((el) => el.uuid === uuid);

              if (idx !== -1) {
                found = true;
                roomChanged = true;
                const newArr = [...space.elements];
                
                // FIX: Don't use spread syntax {...el}, use cloneWithProto to preserve Class instance
                const updatedEl = cloneWithProto(newArr[idx]);
                updatedEl.rotation = rotation;

                newArr[idx] = updatedEl;

                const newSpace = cloneWithProto(space);
                newSpace.elements = newArr;
                return newSpace;
              }
              return space;
            });

            if (roomChanged) {
              const newRoom = cloneWithProto(room);
              newRoom.spaces = newSpaces;
              return newRoom;
            }
            return room;
          });
        }

        if (!found) return prevBuilding;

        return { ...next, isSetByUndoRedo: false, lastAction: EHistoryAction.ELEMENT_ROTATION_CHANGED };
      });
    },
    [setBuilding],
  );

  const setPendingElement = useCallback(
    (element: { type: EElementType; length: number; width: number } | null) => {
      setBuilding((prev) => ({
        ...prev,
        pendingElement: element,
      }));
    },
    [setBuilding]
  );

  return {
    addElement,
    setElements,
    deleteElement,
    updateElement,
    setElementRotation,
    setPendingElement
  };
};